import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../../lib/api';
import { useCurrency } from '../../context/CurrencyContext';
import ProductCard from '../ProductCard';
import ShopFilters from './ShopFilters';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../ui/sheet';

const gridContainer = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const gridItem = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

const SORTS = [
    { value: 'newest', label: 'Newest' },
    { value: 'price-asc', label: 'Price: Low to High' },
    { value: 'price-desc', label: 'Price: High to Low' },
    { value: 'name', label: 'Name: A–Z' },
    { value: 'rating', label: 'Top Rated' },
    { value: 'popular', label: 'Most Reviewed' },
];

const defaultFilters = (bounds, locked) => ({ collections: locked ? [locked] : [], sizes: [], colors: [], inStock: false, minRating: 0, price: bounds });

export default function CatalogBrowser({ lockedCollection = null }) {
    const { format } = useCurrency();
    const [all, setAll] = useState([]);
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [params, setParams] = useSearchParams();
    const [sort, setSort] = useState(params.get('sort') || 'newest');
    const [search, setSearch] = useState(params.get('q') || '');
    const [drawerOpen, setDrawerOpen] = useState(false);

    const priceBounds = useMemo(() => {
        if (all.length === 0) return [0, 1000];
        const prices = all.map((p) => p.price);
        return [Math.floor(Math.min(...prices)), Math.ceil(Math.max(...prices))];
    }, [all]);

    const [filters, setFilters] = useState(defaultFilters([0, 1000], lockedCollection));

    const availSizes = useMemo(() => [...new Set(all.flatMap((p) => p.sizes || []))], [all]);
    const availColors = useMemo(() => [...new Set(all.flatMap((p) => p.colors || []))], [all]);

    useEffect(() => {
        Promise.all([api.get('/products'), api.get('/collections')])
            .then(([pr, cr]) => { setAll(pr.data || []); setCollections(cr.data || []); })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setFilters((f) => ({ ...f, price: priceBounds }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceBounds[0], priceBounds[1]]);

    // reset locked category whenever the route's collection changes
    useEffect(() => {
        setFilters((f) => ({ ...f, collections: lockedCollection ? [lockedCollection] : f.collections }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lockedCollection]);

    useEffect(() => {
        if (lockedCollection) return;
        const c = params.get('collection');
        if (c && c !== 'all') setFilters((f) => ({ ...f, collections: [c] }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const np = {};
        if (sort !== 'newest') np.sort = sort;
        if (search) np.q = search;
        setParams(np, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, search]);

    useEffect(() => {
        const q = params.get('q') || '';
        setSearch((prev) => (prev === q ? prev : q));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    const results = useMemo(() => {
        let data = [...all];
        const q = search.trim().toLowerCase();
        if (q) data = data.filter((p) => `${p.name} ${p.description}`.toLowerCase().includes(q));
        if (filters.collections.length) data = data.filter((p) => (p.collections || []).some((c) => filters.collections.includes(c)));
        if (filters.sizes.length) data = data.filter((p) => (p.sizes || []).some((s) => filters.sizes.includes(s)));
        if (filters.colors.length) data = data.filter((p) => (p.colors || []).some((c) => filters.colors.includes(c)));
        if (filters.inStock) data = data.filter((p) => p.stock_status !== 'out_of_stock');
        if (filters.minRating > 0) data = data.filter((p) => (p.rating_avg || 0) >= filters.minRating);
        data = data.filter((p) => p.price >= filters.price[0] && p.price <= filters.price[1]);

        if (sort === 'price-asc') data.sort((a, b) => a.price - b.price);
        else if (sort === 'price-desc') data.sort((a, b) => b.price - a.price);
        else if (sort === 'name') data.sort((a, b) => a.name.localeCompare(b.name));
        else if (sort === 'rating') data.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
        else if (sort === 'popular') data.sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
        return data;
    }, [all, search, filters, sort]);

    const hasRatings = useMemo(() => all.some((p) => (p.rating_count || 0) > 0), [all]);
    const sortOptions = useMemo(() => SORTS.filter((s) => hasRatings || (s.value !== 'rating' && s.value !== 'popular')), [hasRatings]);

    const facetCollections = filters.collections.filter((c) => c !== lockedCollection);
    const activeCount = facetCollections.length + filters.sizes.length + filters.colors.length
        + (filters.inStock ? 1 : 0) + (filters.minRating > 0 ? 1 : 0)
        + ((filters.price[0] > priceBounds[0] || filters.price[1] < priceBounds[1]) ? 1 : 0);
    const hasAny = activeCount > 0 || !!search.trim();

    const clearAll = () => { setFilters(defaultFilters(priceBounds, lockedCollection)); setSearch(''); };

    const removeChip = (type, val) => {
        if (type === 'price') setFilters({ ...filters, price: priceBounds });
        else if (type === 'inStock') setFilters({ ...filters, inStock: false });
        else if (type === 'minRating') setFilters({ ...filters, minRating: 0 });
        else setFilters({ ...filters, [type]: filters[type].filter((v) => v !== val) });
    };

    const collName = (slug) => collections.find((c) => c.slug === slug)?.name || slug;

    const chips = [
        ...facetCollections.map((c) => ({ type: 'collections', val: c, label: collName(c) })),
        ...filters.sizes.map((s) => ({ type: 'sizes', val: s, label: `Size: ${s}` })),
        ...filters.colors.map((c) => ({ type: 'colors', val: c, label: c })),
        ...(filters.inStock ? [{ type: 'inStock', val: null, label: 'In stock' }] : []),
        ...(filters.minRating > 0 ? [{ type: 'minRating', val: null, label: `${filters.minRating}★ & up` }] : []),
        ...((filters.price[0] > priceBounds[0] || filters.price[1] < priceBounds[1])
            ? [{ type: 'price', val: null, label: `${format(filters.price[0])} – ${format(filters.price[1])}` }] : []),
    ];

    const filtersUI = (
        <ShopFilters collections={collections} sizes={availSizes} colors={availColors} priceBounds={priceBounds} filters={filters} onChange={setFilters} format={format} showRating={hasRatings} showCategory={!lockedCollection} />
    );

    return (
        <>
            {/* Sticky toolbar — hovers just below the header */}
            <div
                className="sticky z-30 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-3 mb-6 bg-[var(--brand-cream,#FBF7F7)]/92 backdrop-blur-xl border-b border-[rgba(90,36,50,0.1)]"
                style={{ top: 'var(--header-h, 88px)' }}
                data-testid="shop-toolbar"
            >
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
                    <div className="relative w-full sm:flex-1">
                        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" style={{ color: 'var(--ink-muted)' }} />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search pieces..."
                            data-testid="shop-search-input"
                            className="input-luxury w-full"
                            style={{ paddingLeft: '2.75rem' }}
                        />
                    </div>

                    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="shop-filters-button"
                                className="lg:hidden inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[rgba(90,36,50,0.18)] bg-white text-xs uppercase tracking-[0.12em] shrink-0"
                                style={{ color: 'var(--brand-burgundy)' }}
                            >
                                <SlidersHorizontal className="w-4 h-4" /> Filters{activeCount > 0 ? ` (${activeCount})` : ''}
                            </button>
                        </SheetTrigger>
                        <SheetContent side="left" className="bg-white w-[86vw] max-w-sm overflow-y-auto" data-testid="shop-filters-drawer">
                            <SheetTitle className="sr-only">Filters</SheetTitle>
                            <div className="flex items-center justify-between mb-2">
                                <p className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>Filters</p>
                                {activeCount > 0 && <button onClick={clearAll} className="text-xs underline" style={{ color: 'var(--brand-burgundy)' }} data-testid="shop-clear-all-mobile">Clear all</button>}
                            </div>
                            {filtersUI}
                            <button onClick={() => setDrawerOpen(false)} className="btn-burgundy w-full mt-5 py-3 rounded-xl text-xs uppercase tracking-[0.18em]" data-testid="shop-apply-filters">Show {results.length} results</button>
                        </SheetContent>
                    </Sheet>

                    <div className="flex items-center gap-2 shrink-0">
                        <span className="eyebrow hidden sm:inline" style={{ color: 'var(--ink-muted)' }}>Sort</span>
                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger data-testid="shop-sort-select" className="min-w-[140px] sm:w-[190px] bg-white border-[rgba(90,36,50,0.18)]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {sortOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {!lockedCollection && (
                    <div className="mt-3 flex gap-2 overflow-x-auto pb-1 shop-chips" data-testid="shop-collection-chips">
                        {[{ slug: 'all', name: 'All' }, ...collections].map((c) => {
                            const active = c.slug === 'all' ? filters.collections.length === 0 : filters.collections.includes(c.slug);
                            return (
                                <button
                                    key={c.slug}
                                    onClick={() => setFilters((f) => ({ ...f, collections: c.slug === 'all' ? [] : (f.collections.includes(c.slug) ? f.collections.filter((x) => x !== c.slug) : [...f.collections, c.slug]) }))}
                                    data-testid={`shop-chip-${c.slug}`}
                                    className="whitespace-nowrap px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] border transition-colors"
                                    style={active
                                        ? { backgroundColor: 'var(--brand-burgundy)', color: '#fff', borderColor: 'var(--brand-burgundy)' }
                                        : { backgroundColor: '#fff', color: 'var(--brand-burgundy)', borderColor: 'rgba(90,36,50,0.18)' }}
                                >
                                    {c.name}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {chips.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="shop-active-filters">
                    {chips.map((ch, i) => (
                        <button
                            key={`${ch.type}-${ch.val}-${i}`}
                            onClick={() => removeChip(ch.type, ch.val)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border"
                            style={{ backgroundColor: 'var(--brand-rose)', borderColor: 'rgba(90,36,50,0.14)', color: 'var(--brand-burgundy)' }}
                        >
                            {ch.label} <X className="w-3 h-3" />
                        </button>
                    ))}
                    <button onClick={clearAll} className="text-xs underline ml-1" style={{ color: 'var(--ink-muted)' }} data-testid="shop-clear-all">Clear all</button>
                </div>
            )}

            <div className="flex gap-8">
                <aside className="hidden lg:block w-64 shrink-0" data-testid="shop-sidebar">
                    <div className="luxury-card p-5 sticky" style={{ top: 'calc(var(--header-h, 88px) + 90px)' }}>
                        <div className="flex items-center justify-between mb-1">
                            <p className="font-serif text-xl" style={{ color: 'var(--brand-burgundy)' }}>Filters</p>
                            {activeCount > 0 && <button onClick={clearAll} className="text-[11px] underline" style={{ color: 'var(--brand-burgundy)' }}>Clear</button>}
                        </div>
                        {filtersUI}
                    </div>
                </aside>

                <div className="flex-1 min-w-0">
                    {!loading && (
                        <p className="text-xs mb-4" style={{ color: 'var(--ink-muted)' }} data-testid="shop-result-count">
                            {results.length} piece{results.length === 1 ? '' : 's'}
                        </p>
                    )}

                    {loading ? (
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="luxury-card animate-pulse">
                                    <div className="aspect-[4/5] bg-[var(--brand-cream)]" />
                                    <div className="p-5"><div className="h-4 w-2/3 bg-[var(--brand-rose)] rounded" /></div>
                                </div>
                            ))}
                        </div>
                    ) : results.length === 0 ? (
                        <div className="py-20 text-center" data-testid="shop-empty-state">
                            <p className="font-serif text-3xl" style={{ color: 'var(--brand-burgundy)' }}>Nothing matches.</p>
                            <p className="mt-2 text-sm" style={{ color: 'var(--ink-muted)' }}>Try removing a filter or searching something else.</p>
                            {hasAny && <button onClick={clearAll} className="btn-burgundy mt-5 px-6 py-3 rounded-xl text-xs uppercase tracking-[0.18em]">Clear all filters</button>}
                        </div>
                    ) : (
                        <motion.div
                            key={`${JSON.stringify(filters)}-${sort}-${search}`}
                            className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-7"
                            data-testid="shop-grid"
                            variants={gridContainer}
                            initial="hidden"
                            animate="show"
                        >
                            {results.map((p) => (
                                <motion.div key={p.id} variants={gridItem}>
                                    <ProductCard product={p} animateSelf={false} />
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>
        </>
    );
}
